import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  AppBar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Skeleton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import SupportIcon from "@mui/icons-material/Support";
import RateReviewIcon from "@mui/icons-material/RateReview";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";

import useUserStore from "../../store/useUserStrore";
import SupportReviewForm from "../SupportReviewForm/SupportReviewForm";
import "./Navbar.css";
import newLogo from "../../assets/images/logonew.jpg";

const Navbar = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:1150px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportFormOpen, setSupportFormOpen] = useState(false);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);

  const { userDetails, userLoading, fetchUserDetails, clearUserDetails } =
    useUserStore();

  const isLoggedIn = !!localStorage.getItem("userToken");
  const popoverOpen = Boolean(anchorEl);

  const isPlanExpired = (date) => {
    if (!date) return true;
    return new Date(date).getTime() < Date.now();
  };

  useEffect(() => {
    if (isLoggedIn) {
      if (!userDetails && !userLoading) {
        fetchUserDetails();
      }
    } else {
      clearUserDetails();
    }
  }, [
    isLoggedIn,
    userDetails,
    userLoading,
    fetchUserDetails,
    clearUserDetails,
  ]);

  const expiredNoticeShown = useRef(false);

  useEffect(() => {
    // Redirection guard removed to allow free search/view of products.
    // Kept removed on purpose: honey-new deleted this deliberately. The
    // ui-polish branch only made the expiry snackbar fire once instead of on
    // every render, so there is nothing to carry over here.
  }, [userDetails, navigate, enqueueSnackbar]);

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    enqueueSnackbar("Logout successful!", { variant: "success" });
    handleClosePopover();
    clearUserDetails();
    navigate("/");
  };

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const goToAuthPage = (mode) => {
    if (mode === "signup") {
      navigate("/signup");
    } else {
      navigate("/");
    }
    handleClosePopover();
    setDrawerOpen(false);
  };

  const handleOpenSupportForm = () => {
    setSupportFormOpen(true);
    handleClosePopover();
    setDrawerOpen(false);
  };

  const handleOpenReviewForm = () => {
    setReviewFormOpen(true);
    handleClosePopover();
    setDrawerOpen(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const isAffiliated = userDetails?.profileType === "affiliated";
  const planExpired = isPlanExpired(userDetails?.premiumValidUpto);

  const isVendor = userDetails?.profileType === "vendor";
  /* Who gets the "Vendor dashboard" entry. Admins do too: the portal now
     accepts them, and an administrator who can approve a listing but has no
     link to the page the listing is built on has to type the URL to see it. */
  const canOpenVendorPortal =
    isVendor || userDetails?.profileType === "admin";

  /* Vendor store leads: it is the one browsing destination here, and the
     three after it are account management. Grouping discovery before
     administration matches the order someone actually uses them in.

     Signed out, the account-management three are dropped. They are all behind
     RequireAuth, so offering them to a visitor is offering three links that
     bounce straight back to sign-in. The vendor store is public and stays -
     it is the whole reason a signed-out visitor is here. */
  const accountItems = [
    { label: "My Team", path: "/myteam" },
    { label: "Products", path: "/products" },
    { label: "Subscription", path: "/subscription" },
  ];

  const desktopMenuItems = isAffiliated
    ? []
    : [
        { label: "Vendor store", path: "/vendor-store" },
        ...(isLoggedIn ? accountItems : []),
      ];

  const drawerMenuItems = isAffiliated
    ? []
    : [
        ...(canOpenVendorPortal
          ? [{ label: "Vendor Dashboard", path: "/vendors" }]
          : []),
        { label: "Vendor store", path: "/vendor-store" },
        ...(isLoggedIn ? accountItems : []),
      ];



  const DrawerList = (
    <Box
      sx={{ width: 250, zIndex: 1300, position: "relative" }}
      role="presentation"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography variant="h6">Menu</Typography>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: "#1a1a1a",
            padding: "8px",
            width: 40,
            height: 40,
            borderRadius: "50%",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "28px", color: "#1a1a1a" }} />
        </IconButton>
      </Box>
      <Divider />

      {isLoggedIn && (
        <>
          <Box sx={{ p: 2 }}>
            {userLoading ? (
              <>
                <Skeleton variant="text" width="80%" height={28} />
                <Skeleton variant="text" width="60%" height={20} />
              </>
            ) : userDetails ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                  {userDetails.fullName || "Unnamed User"}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {userDetails.email}
                </Typography>
                {userDetails.premiumValidUpto && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        color: planExpired ? "error.main" : "success.main",
                      }}
                    >
                      {planExpired ? "Plan Expired" : "Plan Active"}
                    </Typography>
                  </>
                )}
              </>
            ) : null}
          </Box>
          <Divider />
        </>
      )}

      <List>
        {drawerMenuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Signed in only. /plans is behind RequireAuth, so offering an
            upgrade to a visitor with no account sends them to sign-in - and
            it asks someone to upgrade a subscription they do not have. */}
        {isLoggedIn && !isAffiliated && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                navigate("/plans");
                setDrawerOpen(false);
              }}
            >
              <ListItemText primary="Upgrade Now" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <Divider />

      {isLoggedIn && (
        <>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleOpenSupportForm}>
                <ListItemIcon>
                  <SupportIcon />
                </ListItemIcon>
                <ListItemText primary="Support" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleOpenReviewForm}>
                <ListItemIcon>
                  <RateReviewIcon />
                </ListItemIcon>
                <ListItemText primary="Review" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
        </>
      )}

      <List>
        {isLoggedIn ? (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => goToAuthPage("login")}>
                <ListItemText primary="Login" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => goToAuthPage("signup")}>
                <ListItemText primary="Signup" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className="app-navbar"
        sx={{
          color: "#1a1a1a",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        {/* The border lives on the AppBar (see .app-navbar in Navbar.css),
            not on this Paper. The Paper is width-constrainable, so a border
            here could never be guaranteed to span the full viewport. */}
        <Paper
          square
          elevation={0}
          className="app-navbar-surface"
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              height: "auto",
              /* Matches --nav-h, which is also what .app-offset-top reserves
                 below the fixed bar. The two used to be set independently
                 (a 50px logo in a 56px toolbar, plus 8px of wrapper padding
                 either side, measured 89px against a 72px reservation), so
                 the top of every page sat behind the bar. Minus 1px for the
                 bottom border, so the total IS --nav-h. */
              minHeight: "calc(var(--nav-h) - 1px)",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
              onClick={() => navigate("/")}
            >
              <img
                loading="lazy"
                src={newLogo}
                alt="logo"
                style={{ height: 44, display: "block" }}
              />
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={toggleDrawer(true)}
                disableRipple
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px",
                  minWidth: "40px",
                  minHeight: "40px",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "transparent",
                  borderRadius: "10px",
                  border: "1px solid transparent",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e0e0e0",
                  },
                  "& .MuiTouchRipple-root": {
                    display: "none",
                  },
                  /* No `right` offset. The row is justify-content:
                     space-between and this is its last child, so flex
                     already puts it against the right edge. A relative
                     `right: 50px` on top of that pulled it 50px back
                     inward, leaving a gap beside it. */
                }}
              >
                <MenuIcon
                  sx={{
                    fontSize: "28px",
                    color: "#1a1a1a",
                    display: "block",
                    width: "28px",
                    height: "28px",
                  }}
                />
              </IconButton>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "nowrap",
                  minWidth: 0,
                }}
              >
                {desktopMenuItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    sx={{
                      color: "#1a1a1a",
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                      py: 1.5,
                      borderRadius: "10px",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        color: "#1976d2",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}

                {/* Signed in only - see the drawer copy of this button. */}
                {isLoggedIn && !isAffiliated && (
                  <Button
                    variant="contained"
                    onClick={() => navigate("/plans")}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderRadius: "10px",
                      whiteSpace: "nowrap",
                      minWidth: "auto",
                      fontSize: "14px",
                      ml: 1,
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    Upgrade Now
                  </Button>
                )}

                {/* A div with onClick reaches mouse users only. It opens the
                    menu holding Log in and Sign up, which for a signed-out
                    visitor arriving from the marketing site is the only route
                    to an account - so keyboard users had no way in at all. */}
                <Box
                  component="button"
                  type="button"
                  aria-label={isLoggedIn ? "Account menu" : "Log in or sign up"}
                  aria-haspopup="menu"
                  aria-expanded={popoverOpen}
                  onClick={handleOpenPopover}
                  sx={{
                    ml: 2,
                    p: 0,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    transition: "background-color 0.2s",
                  }}
                >
                  <AccountCircleIcon
                    sx={{
                      color: "#1a1a1a",
                      fontSize: 32,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            zIndex: (theme) => theme.zIndex.drawer + 2,
          },
        }}
      >
        {DrawerList}
      </Drawer>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        elevation={0}
        PaperProps={{
          className: "account-menu",
          elevation: 0,
        }}
      >
        {userLoading ? (
          <div className="account-menu-loading">
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={190} height={18} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={36}
              sx={{ mt: 2, borderRadius: "10px" }}
            />
          </div>
        ) : (
          <>
            {userDetails && (
              <div className="account-menu-head">
                <p className="account-name">
                  {userDetails.fullName || "Unnamed User"}
                </p>
                <p className="account-email">{userDetails.email}</p>

                {userDetails.premiumValidUpto && (
                  <span
                    className={`account-plan ${
                      planExpired ? "account-plan-expired" : "account-plan-active"
                    }`}
                  >
                    {planExpired ? "Plan expired" : "Plan active"}
                  </span>
                )}

                {/* Consistent label/value pairs instead of a mix of bare
                    text and "Label: value" strings joined by a <br />. */}
                <dl className="account-meta">
                  {userDetails.premiumValidUpto && (
                    <div>
                      <dt>{planExpired ? "Expired" : "Renews"}</dt>
                      <dd>
                        {new Date(
                          userDetails.premiumValidUpto
                        ).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {userDetails.profileType && (
                    <div>
                      <dt>Role</dt>
                      <dd className="account-role">
                        {userDetails.profileType}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {isLoggedIn ? (
              <>
                <div className="account-menu-list">
                  {/* honey-new added a Vendor Dashboard entry here as a filled
                      MUI button. Kept the destination, dropped the button
                      treatment: one row style for every entry in this menu. */}
                  {canOpenVendorPortal && (
                    <button
                      type="button"
                      className="account-item"
                      onClick={() => {
                        navigate("/vendors");
                        handleClosePopover();
                      }}
                    >
                      <DashboardIcon className="account-item-icon" />
                      <span>Vendor dashboard</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="account-item"
                    onClick={handleOpenSupportForm}
                  >
                    <SupportIcon className="account-item-icon" />
                    <span>Support</span>
                  </button>
                  <button
                    type="button"
                    className="account-item"
                    onClick={handleOpenReviewForm}
                  >
                    <RateReviewIcon className="account-item-icon" />
                    <span>Leave a review</span>
                  </button>
                </div>

                <div className="account-menu-list account-menu-list-last">
                  <button
                    type="button"
                    className="account-item account-item-danger"
                    onClick={handleLogout}
                  >
                    <LogoutIcon className="account-item-icon" />
                    <span>Log out</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="account-menu-list account-menu-list-last">
                <button
                  type="button"
                  className="account-item"
                  onClick={() => goToAuthPage("login")}
                >
                  <span>Log in</span>
                </button>
                <button
                  type="button"
                  className="account-item"
                  onClick={() => goToAuthPage("signup")}
                >
                  <span>Sign up</span>
                </button>
              </div>
            )}
          </>
        )}
      </Popover>

      <SupportReviewForm
        open={supportFormOpen}
        onClose={() => setSupportFormOpen(false)}
        type="feedback"
        userDetails={userDetails}
      />
      <SupportReviewForm
        open={reviewFormOpen}
        onClose={() => setReviewFormOpen(false)}
        type="review"
        userDetails={userDetails}
      />
    </>
  );
};

export default Navbar;
